import React, { useState, useEffect } from 'react';
import { useApi } from '../../hooks/useApi';
import { User } from '../../types';
import { isStandardApiResponse } from '../../types/api';

interface UserStatistics {
  total_storage_mb: number;
  total_files: number;
  total_projects: number;
  total_data_count: number;
  last_activity: string | null;
  subscription: {
    plan_name: string;
    max_storage_mb: number;
    usage_percent: number;
  } | null;
}

interface SubscriptionPlan {
  id: number;
  name: string;
  display_name: string;
  description: string;
  price_monthly: number;
  formatted_price: string;
  max_storage_gb: number;
  max_projects: number | null;
  max_data_per_project: number | null;
  api_access: boolean;
  allowed_file_formats: string;
}

interface SubscriptionPlansResponse {
  plans: SubscriptionPlan[];
}

interface UserUpdateData {
  username?: string;
  email?: string;
  first_name?: string;
  last_name?: string;
  display_name?: string;
  organization?: string;
  is_active?: boolean;
  is_staff?: boolean;
  is_superuser?: boolean;
  subscription_plan_id?: string | number;
}

interface DeleteUserData {
  confirm_superuser_delete?: boolean;
}

interface UserWithStats {
  user_id: number;
  username: string;
  email: string;
  first_name: string | null;
  last_name: string | null;
  display_name: string;
  organization: string | null;
  is_staff: boolean;
  is_superuser: boolean;
  is_active: boolean;
  date_joined: string;
  last_login: string | null;
  statistics: UserStatistics;
  limits?: {
    status?: {
      storage?: 'OK' | 'WARNING' | 'CRITICAL';
      projects?: 'OK' | 'WARNING' | 'CRITICAL';
      overall?: 'OK' | 'WARNING' | 'CRITICAL';
    };
    percentages?: {
      storage?: number;
    };
    usage?: {
      projects?: number;
    };
    limits?: {
      projects?: number;
    };
  };
}

interface UserCardProps {
  user?: User; // 従来のUser型（後方互換性）
  userWithStats?: UserWithStats; // 新しい統計付きユーザー型
  onUserUpdated: () => void;
}

/**
 * ユーザーカード
 * 横長カード形式でユーザー情報を表示、クリックで編集フォーム展開
 */
const UserCard: React.FC<UserCardProps> = ({ user, userWithStats, onUserUpdated }) => {
  const { execute: apiCall } = useApi();
  
  // 統計データがある場合はそれを優先して使用
  const displayUser = userWithStats ? {
    id: userWithStats.user_id,
    username: userWithStats.username,
    email: userWithStats.email,
    first_name: userWithStats.first_name || '',
    last_name: userWithStats.last_name || '',
    display_name: userWithStats.display_name,
    organization: userWithStats.organization || '',
    is_staff: userWithStats.is_staff,
    is_superuser: userWithStats.is_superuser,
    is_active: userWithStats.is_active,
    date_joined: userWithStats.date_joined,
    last_login: userWithStats.last_login,
    // 統計データから推定される値
    storage_usage_mb: userWithStats.statistics.total_storage_mb,
    storage_usage_percentage: userWithStats.statistics.subscription?.usage_percent || 0,
    subscription_plan: userWithStats.statistics.subscription?.plan_name || '',
    max_storage_gb: userWithStats.statistics.subscription?.max_storage_mb ? 
      Math.round(userWithStats.statistics.subscription.max_storage_mb / 1024) : undefined,
    // 統計データを直接アクセス可能にする
    statistics: userWithStats.statistics
  } as User & { statistics: UserStatistics } : user!;
  
  const [isExpanded, setIsExpanded] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [editingSection, setEditingSection] = useState<'basic' | 'subscription' | null>(null);
  const [subscriptionPlans, setSubscriptionPlans] = useState<SubscriptionPlan[]>([]);
  const [plansLoading, setPlansLoading] = useState(false);
  const [validationErrors, setValidationErrors] = useState<{[key: string]: string[]}>({});
  
  // 編集用フォームデータ
  const [editData, setEditData] = useState({
    username: displayUser.username,
    email: displayUser.email,
    first_name: displayUser.first_name || '',
    last_name: displayUser.last_name || '',
    organization: displayUser.organization || '',
    is_active: displayUser.is_active,
    is_staff: displayUser.is_staff,
    is_superuser: displayUser.is_superuser,
    subscription_plan_id: displayUser.subscription_plan_id || ''
  });

  // 日時フォーマット
  const formatDate = (dateString: string | null) => {
    if (!dateString) return '未ログイン';
    return new Date(dateString).toLocaleString('ja-JP', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };



  // ストレージ表示用フォーマット
  const formatStorage = (mb: number) => {
    if (mb >= 1024) {
      return `${(mb / 1024).toFixed(1)}GB`;
    }
    return `${mb}MB`;
  };

  // サブスクリプションプラン一覧を取得
  const fetchSubscriptionPlans = async () => {
    try {
      setPlansLoading(true);
      const response = await apiCall('/api/plans/', {
        method: 'GET'
      });
      
      if (isStandardApiResponse(response) && response.status === 'success') {
        const data = response.data as SubscriptionPlansResponse;
        setSubscriptionPlans(data?.plans || []);
      } else if (response && typeof response === 'object' && 'plans' in response) {
        const data = response as SubscriptionPlansResponse;
        setSubscriptionPlans(data.plans || []);
      }
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('サブスクリプションプラン取得に失敗しました:', error);
      }
    } finally {
      setPlansLoading(false);
    }
  };

  // コンポーネントマウント時にプラン一覧を取得
  useEffect(() => {
    fetchSubscriptionPlans();
  }, []);

  // バリデーション関数
  const validateForm = (): boolean => {
    const errors: {[key: string]: string[]} = {};

    // ユーザー名バリデーション
    if (!editData.username.trim()) {
      errors['username'] = ['ユーザー名は必須です'];
    } else if (editData.username.length > 150) {
      errors['username'] = ['ユーザー名は150文字以下である必要があります'];
    } else {
      // 文字ごとに許可された文字かチェック
      const allowedChars = editData.username.split('').every(char => {
        const code = char.charCodeAt(0);
        return (
          // 英数字
          (code >= 48 && code <= 57) ||   // 0-9
          (code >= 65 && code <= 90) ||   // A-Z
          (code >= 97 && code <= 122) ||  // a-z
          // 記号
          char === '.' || char === '@' || char === '+' || char === '-' || char === '_' ||
          // ひらがな
          (code >= 0x3040 && code <= 0x309F) ||
          // カタカナ
          (code >= 0x30A0 && code <= 0x30FF) ||
          // 漢字（全範囲）
          (code >= 0x4E00 && code <= 0x9FFF)
        );
      });
      
      if (!allowedChars) {
        errors['username'] = ['ユーザー名は英数字、日本語文字（ひらがな・カタカナ・漢字）、@、.、+、-、_のみ使用可能です'];
      }
    }

    // メールアドレスバリデーション
    if (!editData.email.trim()) {
      errors['email'] = ['メールアドレスは必須です'];
    } else {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(editData.email)) {
        errors['email'] = ['有効なメールアドレスを入力してください'];
      } else if (editData.email.length > 254) {
        errors['email'] = ['メールアドレスは254文字以下である必要があります'];
      }
    }

    // 姓名バリデーション
    if (editData.first_name && editData.first_name.length > 150) {
      errors['first_name'] = ['名前は150文字以下である必要があります'];
    }
    if (editData.last_name && editData.last_name.length > 150) {
      errors['last_name'] = ['姓は150文字以下である必要があります'];
    }

    // 組織名バリデーション
    if (editData.organization && editData.organization.length > 100) {
      errors['organization'] = ['組織名は100文字以下である必要があります'];
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // リアルタイムバリデーション
  const validateField = (fieldName: string, value: string) => {
    const fieldErrors: {[key: string]: string[]} = { ...validationErrors };

    switch (fieldName) {
      case 'username':
        if (!value.trim()) {
          fieldErrors['username'] = ['ユーザー名は必須です'];
        } else if (value.length > 150) {
          fieldErrors['username'] = ['ユーザー名は150文字以下である必要があります'];
        } else {
          // 文字ごとに許可された文字かチェック
          const allowedChars = value.split('').every(char => {
            const code = char.charCodeAt(0);
            return (
              // 英数字
              (code >= 48 && code <= 57) ||   // 0-9
              (code >= 65 && code <= 90) ||   // A-Z
              (code >= 97 && code <= 122) ||  // a-z
              // 記号
              char === '.' || char === '@' || char === '+' || char === '-' || char === '_' ||
              // ひらがな
              (code >= 0x3040 && code <= 0x309F) ||
              // カタカナ
              (code >= 0x30A0 && code <= 0x30FF) ||
              // 漢字（全範囲）
              (code >= 0x4E00 && code <= 0x9FFF)
            );
          });
          
          if (!allowedChars) {
            fieldErrors['username'] = ['ユーザー名は英数字、日本語文字（ひらがな・カタカナ・漢字）、@、.、+、-、_のみ使用可能です'];
          } else {
            delete fieldErrors['username'];
          }
        }
        break;

      case 'email':
        if (!value.trim()) {
          fieldErrors['email'] = ['メールアドレスは必須です'];
        } else {
          const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
          if (!emailRegex.test(value)) {
            fieldErrors['email'] = ['有効なメールアドレスを入力してください'];
          } else if (value.length > 254) {
            fieldErrors['email'] = ['メールアドレスは254文字以下である必要があります'];
          } else {
            delete fieldErrors['email'];
          }
        }
        break;

      case 'first_name':
        if (value && value.length > 150) {
          fieldErrors['first_name'] = ['名前は150文字以下である必要があります'];
        } else {
          delete fieldErrors['first_name'];
        }
        break;

      case 'last_name':
        if (value && value.length > 150) {
          fieldErrors['last_name'] = ['姓は150文字以下である必要があります'];
        } else {
          delete fieldErrors['last_name'];
        }
        break;

      case 'organization':
        if (value && value.length > 100) {
          fieldErrors['organization'] = ['組織名は100文字以下である必要があります'];
        } else {
          delete fieldErrors['organization'];
        }
        break;
    }

    setValidationErrors(fieldErrors);
  };

  // カード展開/収納
  const handleCardClick = () => {
    setIsExpanded(!isExpanded);
  };

  // 編集キャンセル
  const handleEditCancel = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsEditing(false);
    setEditingSection(null);
    setValidationErrors({}); // バリデーションエラーをクリア
    // 編集データを元に戻す
    setEditData({
      username: displayUser.username,
      email: displayUser.email,
      first_name: displayUser.first_name || '',
      last_name: displayUser.last_name || '',
      organization: displayUser.organization || '',
      is_active: displayUser.is_active,
      is_staff: displayUser.is_staff,
      is_superuser: displayUser.is_superuser,
      subscription_plan_id: (displayUser as any).statistics?.subscription?.plan_name || ''
    });
  };

  // 編集保存
  const handleEditSave = async (e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    
    // バリデーションチェック
    if (!validateForm()) {
      // バリデーションエラーをアラートで表示
      const errorMessages = Object.entries(validationErrors)
        .map(([field, errors]) => {
          const fieldNames: {[key: string]: string} = {
            'username': 'ユーザー名',
            'email': 'メールアドレス',
            'first_name': '名前',
            'last_name': '姓',
            'organization': '組織名'
          };
          return `【${fieldNames[field] || field}】\n${errors.join('\n')}`;
        })
        .join('\n\n');
      
      alert(`入力内容にエラーがあります:\n\n${errorMessages}`);
      return;
    }

    try {
      setLoading(true);
      const token = localStorage.getItem('authToken');
      const cleanToken = token ? token.replace(/"/g, '') : '';
      
      // セクション別編集の場合
      if (editingSection) {
        let updateData: UserUpdateData = {};
        
        if (editingSection === 'basic') {
          updateData = {
            username: editData.username,
            email: editData.email,
            first_name: editData.first_name,
            last_name: editData.last_name,
            organization: editData.organization,
            is_active: editData.is_active,
            is_staff: editData.is_staff,
            is_superuser: editData.is_superuser
          };
        } else if (editingSection === 'subscription') {
          updateData = {
            subscription_plan_id: editData.subscription_plan_id
          };
        }
        
        await apiCall(`/api/users/${displayUser.id}/`, {
          method: 'PUT',
          headers: { 
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${cleanToken}`
          },
          body: JSON.stringify(updateData)
        });
        
        setEditingSection(null);
      } else {
        // 全体編集の場合
        await apiCall(`/api/users/${displayUser.id}/`, {
          method: 'PUT',
          headers: { 
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${cleanToken}`
          },
          body: JSON.stringify(editData)
        });
        setIsEditing(false);
      }
      
      // バリデーションエラーをクリア
      setValidationErrors({});
      onUserUpdated();
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('ユーザー更新に失敗しました:', error);
      }
      alert('ユーザー更新に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  // 削除確認
  const handleDeleteConfirm = (e: React.MouseEvent) => {
    e.stopPropagation();
    
    // 削除アラートを表示
    const displayName = displayUser.first_name && displayUser.last_name 
      ? `${displayUser.last_name} ${displayUser.first_name}` 
      : displayUser.username;
    
    let alertMessage = `ユーザー「${displayName}」を削除しますか？\n\nこの操作は取り消すことができません。`;
    
    // スーパーユーザーの場合は追加警告
    if (displayUser.is_superuser) {
      alertMessage += `\n\n⚠️ 警告: このユーザーはスーパーユーザーです。\n削除すると管理者権限が失われる可能性があります。`;
    }
    
    // アクティブなサブスクリプションがある場合の警告
    if (displayUser.subscription_plan) {
      alertMessage += `\n\n📋 このユーザーは「${displayUser.subscription_plan}」をご利用中です。\n削除するとサブスクリプションも無効になります。`;
    }
    
    if (window.confirm(alertMessage)) {
      setDeleteConfirm(true);
    }
  };

  // 削除実行
  const handleDelete = async (e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      setLoading(true);
      const token = localStorage.getItem('authToken');
      const cleanToken = token ? token.replace(/"/g, '') : '';
      
      const deleteData: DeleteUserData = {};
      if (displayUser.is_superuser) {
        deleteData.confirm_superuser_delete = true;
      }
      await apiCall(`/api/users/${displayUser.id}/`, {
        method: 'DELETE',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${cleanToken}`
        },
        body: JSON.stringify(deleteData)
      });
      
      // 削除成功メッセージ
      const displayName = displayUser.first_name && displayUser.last_name 
        ? `${displayUser.last_name} ${displayUser.first_name}` 
        : displayUser.username;
      alert(`ユーザー「${displayName}」を削除しました。`);
      
      onUserUpdated();
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('ユーザー削除に失敗しました:', error);
      }
      alert('ユーザー削除に失敗しました');
    } finally {
      setLoading(false);
      setDeleteConfirm(false);
    }
  };

  // 権限バッジは削除（カードの境界線とブラックアウトで有効/無効を表示）

  return (
    <div className={`user-card ${isExpanded ? 'expanded' : ''} ${!displayUser.is_active ? 'inactive' : ''}`}>
      
      {/* メインカード */}
      <div className="user-card-main" onClick={handleCardClick}>
                <div className="row align-items-center">
          <div className="col-3">
            <div className="user-name-section">
              <h3 className="mb-0 fw-bold">{displayUser.username}</h3>
            </div>
          </div>
          <div className="col-3">
            <div className="user-details-section">
              <div className="user-fullname fw-medium text-dark">{displayUser.last_name} {displayUser.first_name}</div>
              <div className="user-email">
                <small className="text-muted">{displayUser.email}</small>
              </div>
            </div>
          </div>
          <div className="col-2">
            <div className="user-subscription">
              {displayUser.subscription_plan ? (
                <span className={`badge subscription-badge ${
                  displayUser.subscription_plan.includes('LITE') ? 'badge-lite' :
                  displayUser.subscription_plan.includes('STUDIO') ? 'badge-studio' :
                  displayUser.subscription_plan.includes('VIP') ? 'badge-vip' :
                  displayUser.subscription_plan.includes('管理者') ? 'badge-admin' :
                  'bg-secondary'
                } me-1`}>
                  {displayUser.subscription_plan.replace('プラン', '')}
                </span>
              ) : (
                <span className="badge subscription-badge bg-light text-muted">
                  <i className="fas fa-minus me-1"></i>未登録
                </span>
              )}
            </div>
          </div>
          <div className="col-3">
            <div className="user-storage">
              {userWithStats ? (
                // 統計データがある場合の表示
                <div>
                  <small className="text-muted">
                    {formatStorage(userWithStats.statistics.total_storage_mb)} / 
                    {userWithStats.statistics.subscription?.max_storage_mb ? 
                      formatStorage(userWithStats.statistics.subscription.max_storage_mb) : 
                      '不明'
                    } ({userWithStats.statistics.subscription?.usage_percent || 0}%)
                  </small>
                  <div className="progress progress-sm mt-1">
                    <div 
                      className={`progress-bar ${
                        (userWithStats.statistics.subscription?.usage_percent || 0) > 80 ? 'bg-danger' : 'bg-secondary'
                      }`}
                      style={{width: `${Math.min(userWithStats.statistics.subscription?.usage_percent || 0, 100)}%`}}
                    ></div>
                  </div>
                </div>
              ) : displayUser.storage_usage_mb !== undefined && displayUser.max_storage_gb ? (
                // フォールバック表示
                <div>
                  <small className="text-muted">
                    {formatStorage(displayUser.storage_usage_mb)} / {displayUser.max_storage_gb}GB ({displayUser.storage_usage_percentage}%)
                  </small>
                  <div className="progress progress-sm mt-1">
                    <div 
                      className={`progress-bar ${
                        (displayUser.storage_usage_percentage || 0) > 80 ? 'bg-danger' : 'bg-secondary'
                      }`}
                      style={{width: `${Math.min(displayUser.storage_usage_percentage || 0, 100)}%`}}
                    ></div>
                  </div>
                </div>
              ) : (
                <small className="text-muted">未設定</small>
              )}
            </div>
          </div>
          <div className="col-1 text-end">
            <i className={`fas fa-chevron-${isExpanded ? 'up' : 'down'} text-muted`}></i>
          </div>
        </div>
      </div>

      {/* 展開部分 */}
      {isExpanded && (
        <div className="user-card-details">
          <div className="border-top pt-3">
            {isEditing ? (
              /* 編集フォーム */
              <div className="edit-form">
                <div className="row">
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label className="form-label">ユーザー名</label>
                      <input
                        type="text"
                        className="form-control"
                        value={editData.username}
                        onChange={(e) => setEditData({...editData, username: e.target.value})}
                      />
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label className="form-label">メールアドレス</label>
                      <input
                        type="email"
                        className="form-control"
                        value={editData.email}
                        onChange={(e) => setEditData({...editData, email: e.target.value})}
                      />
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label className="form-label">姓</label>
                      <input
                        type="text"
                        className="form-control"
                        value={editData.last_name}
                        onChange={(e) => setEditData({...editData, last_name: e.target.value})}
                      />
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label className="form-label">名</label>
                      <input
                        type="text"
                        className="form-control"
                        value={editData.first_name}
                        onChange={(e) => setEditData({...editData, first_name: e.target.value})}
                      />
                    </div>
                  </div>
                  <div className="col-md-12">
                    <div className="mb-3">
                      <label className="form-label">所属組織</label>
                      <input
                        type="text"
                        className="form-control"
                        value={editData.organization}
                        onChange={(e) => setEditData({...editData, organization: e.target.value})}
                      />
                    </div>
                  </div>
                  <div className="col-md-12">
                    <div className="mb-3">
                      <div className="form-check form-check-inline">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          checked={editData.is_active}
                          onChange={(e) => setEditData({...editData, is_active: e.target.checked})}
                        />
                        <label className="form-check-label">アクティブ</label>
                      </div>
                      <div className="form-check form-check-inline">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          checked={editData.is_staff}
                          onChange={(e) => setEditData({...editData, is_staff: e.target.checked})}
                        />
                        <label className="form-check-label">スタッフ権限</label>
                      </div>
                      <div className="form-check form-check-inline">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          checked={editData.is_superuser}
                          onChange={(e) => setEditData({...editData, is_superuser: e.target.checked})}
                        />
                        <label className="form-check-label">スーパーユーザー権限</label>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="d-flex justify-content-end gap-2">
                  <button
                    className="btn btn-admin-cancel"
                    onClick={handleEditCancel}
                    disabled={loading}
                  >
                    キャンセル
                  </button>
                  <button
                    className="btn btn-admin-save"
                    onClick={handleEditSave}
                    disabled={loading}
                  >
                    {loading ? '保存中...' : '保存'}
                  </button>
                </div>
              </div>
            ) : (
              /* 詳細表示 */
              <div className="details-view">
                <div className="row g-4">
                  <div className="col-md-6">
                    <div className="detail-section">
                      <div className="d-flex justify-content-between align-items-center mb-3">
                        <h6 className="detail-section-title mb-0">基本情報</h6>
                        {editingSection !== 'basic' && (
                          <button
                            className="btn btn-admin-edit btn-sm"
                            onClick={() => setEditingSection('basic')}
                          >
                            <i className="fas fa-edit me-1"></i>編集
                          </button>
                        )}
                      </div>
                      <div className="detail-items">
                        <div className="detail-item">
                          <span className="detail-label">ユーザーID</span>
                          <span className="detail-value">{displayUser.id}</span>
                        </div>
                        <div className="detail-item">
                          <span className="detail-label">ユーザー名</span>
                          {editingSection === 'basic' ? (
                            <div className="detail-edit-form">
                              <input
                                type="text"
                                className={`form-control form-control-sm ${validationErrors.username ? 'is-invalid' : ''}`}
                                value={editData.username}
                                onChange={(e) => {
                                  setEditData({...editData, username: e.target.value});
                                  validateField('username', e.target.value);
                                }}
                                placeholder="ユーザー名"
                              />
                              {validationErrors.username && (
                                <div className="invalid-feedback">
                                  {validationErrors.username.join(', ')}
                                </div>
                              )}
                            </div>
                          ) : (
                            <span className="detail-value">{displayUser.username}</span>
                          )}
                        </div>
                        <div className="detail-item">
                          <span className="detail-label">メールアドレス</span>
                          {editingSection === 'basic' ? (
                            <div className="detail-edit-form">
                              <input
                                type="email"
                                className={`form-control form-control-sm ${validationErrors.email ? 'is-invalid' : ''}`}
                                value={editData.email}
                                onChange={(e) => {
                                  setEditData({...editData, email: e.target.value});
                                  validateField('email', e.target.value);
                                }}
                                placeholder="メールアドレス"
                              />
                              {validationErrors.email && (
                                <div className="invalid-feedback">
                                  {validationErrors.email.join(', ')}
                                </div>
                              )}
                            </div>
                          ) : (
                            <span className="detail-value">{displayUser.email}</span>
                          )}
                        </div>
                        <div className="detail-item">
                          <span className="detail-label">姓</span>
                          {editingSection === 'basic' ? (
                            <div className="detail-edit-form">
                              <input
                                type="text"
                                className={`form-control form-control-sm ${validationErrors.last_name ? 'is-invalid' : ''}`}
                                value={editData.last_name}
                                onChange={(e) => {
                                  setEditData({...editData, last_name: e.target.value});
                                  validateField('last_name', e.target.value);
                                }}
                                placeholder="姓"
                              />
                              {validationErrors.last_name && (
                                <div className="invalid-feedback">
                                  {validationErrors.last_name.join(', ')}
                                </div>
                              )}
                            </div>
                          ) : (
                            <span className="detail-value">{displayUser.last_name || '未設定'}</span>
                          )}
                        </div>
                        <div className="detail-item">
                          <span className="detail-label">名</span>
                          {editingSection === 'basic' ? (
                            <div className="detail-edit-form">
                              <input
                                type="text"
                                className={`form-control form-control-sm ${validationErrors.first_name ? 'is-invalid' : ''}`}
                                value={editData.first_name}
                                onChange={(e) => {
                                  setEditData({...editData, first_name: e.target.value});
                                  validateField('first_name', e.target.value);
                                }}
                                placeholder="名"
                              />
                              {validationErrors.first_name && (
                                <div className="invalid-feedback">
                                  {validationErrors.first_name.join(', ')}
                                </div>
                              )}
                            </div>
                          ) : (
                            <span className="detail-value">{displayUser.first_name || '未設定'}</span>
                          )}
                        </div>
                        <div className="detail-item">
                          <span className="detail-label">所属組織</span>
                          {editingSection === 'basic' ? (
                            <div className="detail-edit-form">
                              <input
                                type="text"
                                className={`form-control form-control-sm ${validationErrors.organization ? 'is-invalid' : ''}`}
                                value={editData.organization}
                                onChange={(e) => {
                                  setEditData({...editData, organization: e.target.value});
                                  validateField('organization', e.target.value);
                                }}
                                placeholder="所属組織"
                              />
                              {validationErrors.organization && (
                                <div className="invalid-feedback">
                                  {validationErrors.organization.join(', ')}
                                </div>
                              )}
                            </div>
                          ) : (
                            <span className="detail-value">{displayUser.organization || '未設定'}</span>
                          )}
                        </div>
                        <div className="detail-item">
                          <span className="detail-label">登録日</span>
                          <span className="detail-value">{formatDate(displayUser.date_joined)}</span>
                        </div>
                        <div className="detail-item">
                          <span className="detail-label">最終ログイン</span>
                          <span className="detail-value">{formatDate(displayUser.last_login)}</span>
                        </div>
                        <div className="detail-item">
                          <span className="detail-label">アカウント状態</span>
                          {editingSection === 'basic' ? (
                            <div className="detail-edit-form">
                              <select
                                className="form-select form-select-sm"
                                value={editData.is_active ? 'active' : 'inactive'}
                                onChange={(e) => setEditData({...editData, is_active: e.target.value === 'active'})}
                              >
                                <option value="active">アクティブ</option>
                                <option value="inactive">無効</option>
                              </select>
                            </div>
                          ) : (
                            <span>
                              {displayUser.is_active ? 'アクティブ' : '無効'}
                            </span>
                          )}
                        </div>
                        {editingSection === 'basic' && (
                          <div className="detail-edit-actions">
                            <button
                              className="btn btn-admin-cancel btn-sm me-2"
                              onClick={() => setEditingSection(null)}
                            >
                              キャンセル
                            </button>
                            <button
                              className="btn btn-admin-save btn-sm"
                              onClick={handleEditSave}
                              disabled={loading}
                            >
                              {loading ? '保存中...' : '保存'}
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="detail-section">
                      <div className="d-flex justify-content-between align-items-center mb-3">
                        <h6 className="detail-section-title mb-0">サブスクリプション</h6>
                        {editingSection !== 'subscription' && (
                          <button
                            className="btn btn-admin-edit btn-sm"
                            onClick={() => setEditingSection('subscription')}
                          >
                            <i className="fas fa-edit me-1"></i>編集
                          </button>
                        )}
                      </div>
                      <div className="detail-items">
                        <div className="detail-item">
                          <span className="detail-label">プラン</span>
                          {editingSection === 'subscription' ? (
                            <div className="detail-edit-form">
                              <select 
                                className="form-select form-select-sm"
                                value={editData.subscription_plan_id || ''}
                                onChange={(e) => {
                                  setEditData({...editData, subscription_plan_id: e.target.value});
                                }}
                                disabled={plansLoading}
                              >
                                <option value="">
                                  {plansLoading ? 'プラン読み込み中...' : 'プランを選択'}
                                </option>
                                {subscriptionPlans.map(plan => (
                                  <option key={plan.id} value={plan.id}>
                                    {plan.display_name} ({plan.formatted_price})
                                  </option>
                                ))}
                              </select>
                            </div>
                          ) : (
                            <div className="detail-value">
                              {displayUser.subscription_plan ? (
                                <span>
                                  {displayUser.subscription_plan}
                                </span>
                              ) : (
                                <span className="text-muted">未登録</span>
                              )}
                            </div>
                          )}
                        </div>
                        <div className="detail-item">
                          <span className="detail-label">ストレージ使用量</span>
                          <div className="detail-value">
                            {userWithStats && userWithStats.statistics ? (
                              <div>
                                <span>
                                  {formatStorage(userWithStats.statistics.total_storage_mb)} / 
                                  {userWithStats.statistics.subscription?.max_storage_mb ? 
                                    formatStorage(userWithStats.statistics.subscription.max_storage_mb) : 
                                    '不明'
                                  } ({userWithStats.statistics.subscription?.usage_percent || 0}%)
                                </span>
                                <br />
                                <small className="text-info">
                                  プラン: {userWithStats.statistics.subscription?.plan_name || '不明'}
                                </small>
                              </div>
                            ) : (
                              <span className="text-muted">データなし</span>
                            )}
                          </div>
                        </div>

                        {/* 制限チェック情報 */}
                        {userWithStats && userWithStats.limits && (
                          <div className="detail-item">
                            <span className="detail-label">制限チェック</span>
                            <div className="detail-value">
                              <div className="limits-status mb-2">
                                <div className="d-flex justify-content-between align-items-center mb-1">
                                  <span className="small">ストレージ</span>
                                  {userWithStats.limits.status?.storage === 'OK' ? (
                                    <span className="text-muted">
                                      {userWithStats.limits.percentages?.storage || 0}%
                                    </span>
                                  ) : (
                                    <span className="badge bg-danger">
                                      {userWithStats.limits.percentages?.storage || 0}%
                                    </span>
                                  )}
                                </div>
                                {userWithStats.limits.limits?.projects && (
                                  <div className="d-flex justify-content-between align-items-center mb-1">
                                    <span className="small">プロジェクト</span>
                                    {userWithStats.limits.status?.projects === 'OK' ? (
                                      <span className="text-muted">
                                        {userWithStats.limits.usage?.projects || 0} / {userWithStats.limits.limits.projects}
                                      </span>
                                    ) : (
                                      <span className="badge bg-danger">
                                        {userWithStats.limits.usage?.projects || 0} / {userWithStats.limits.limits.projects}
                                      </span>
                                    )}
                                  </div>
                                )}
                                <div className="d-flex justify-content-between align-items-center">
                                  <span className="small">総合ステータス</span>
                                  {userWithStats.limits.status?.overall === 'OK' ? (
                                    <span className="text-muted">正常</span>
                                  ) : (
                                    <span className="badge bg-warning">制限超過</span>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        )}
                        
                        {/* プロジェクト・データ統計 */}
                        {userWithStats && userWithStats.statistics && (
                          <>
                            <div className="detail-item">
                              <span className="detail-label">プロジェクト数</span>
                              <div className="detail-value">
                                <span className="fw-medium">
                                  {userWithStats.statistics.total_projects || 0}個
                                </span>
                                {userWithStats.statistics.total_projects > 0 && (
                                  <small className="text-muted ms-2">
                                    データ総数: {userWithStats.statistics.total_data_count || 0}件
                                  </small>
                                )}
                              </div>
                            </div>
                            
                            <div className="detail-item">
                              <span className="detail-label">総ファイル数</span>
                              <div className="detail-value">
                                <span className="fw-medium">
                                  {userWithStats.statistics.total_files || 0}件
                                </span>
                              </div>
                            </div>
                          </>
                        )}
                        {editingSection === 'subscription' && (
                          <div className="detail-edit-actions">
                            <button
                              className="btn btn-admin-cancel btn-sm me-2"
                              onClick={() => setEditingSection(null)}
                            >
                              キャンセル
                            </button>
                            <button
                              className="btn btn-admin-save btn-sm"
                              onClick={handleEditSave}
                              disabled={loading}
                            >
                              {loading ? '保存中...' : '保存'}
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="d-flex justify-content-end gap-2 mt-3">
                  {deleteConfirm ? (
                    <>
                      <button
                        className="btn btn-admin-cancel btn-sm"
                        onClick={(e) => { e.stopPropagation(); setDeleteConfirm(false); }}
                      >
                        キャンセル
                      </button>
                      <button
                        className="btn btn-admin-delete btn-sm"
                        onClick={handleDelete}
                        disabled={loading}
                      >
                        {loading ? 'ユーザー削除中...' : 'ユーザー削除'}
                      </button>
                    </>
                  ) : (
                    <button
                      className="btn btn-admin-delete-confirm btn-sm"
                      onClick={handleDeleteConfirm}
                    >
                      <i className="fas fa-trash me-1"></i>
                      ユーザー削除
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default UserCard; 